import type { GetAttachmentConversionResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { attachmentsKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

interface UseVideoConversionResult {
  convertedUrl: null | string;
  status: "converting" | "ready" | "unavailable";
}

export interface UseVideoConversionOptions {
  maxAttempts?: number;
  pollIntervalMs?: number;
}

export function useVideoConversion(
  attachmentId: number,
  enabled: boolean,
  {
    maxAttempts = MAX_POLL_ATTEMPTS,
    pollIntervalMs = POLL_INTERVAL_MS,
  }: UseVideoConversionOptions = {},
): UseVideoConversionResult {
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    setAttempts(0);
  }, [attachmentId]);

  const { data, dataUpdatedAt } = useQuery({
    enabled,
    queryFn: () =>
      apiClient
        .url(`/attachments/conversion/${attachmentId}`)
        .get()
        .json<GetAttachmentConversionResponse>(),
    queryKey: attachmentsKeys.getConversions(attachmentId),
    refetchInterval: (query) =>
      query.state.data?.length || attempts >= maxAttempts
        ? false
        : pollIntervalMs,
  });

  useEffect(() => {
    if (!enabled || data?.length || dataUpdatedAt === 0) return;

    setAttempts((prev) => prev + 1);
  }, [dataUpdatedAt, data?.length, enabled]);

  const convertedUrl = data?.[0]?.url ?? null;

  if (!enabled) return { convertedUrl: null, status: "converting" };
  if (convertedUrl) return { convertedUrl, status: "ready" };
  if (attempts >= maxAttempts) {
    return { convertedUrl: null, status: "unavailable" };
  }

  return { convertedUrl: null, status: "converting" };
}
